import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useAuth } from "../lib/auth-context.js";
import { ApiError } from "../lib/api.js";
import {
  createWatchlist,
  deleteWatchlist,
  listWatchlists,
  normalizeTickerInput,
  updateWatchlist,
} from "../lib/api-modules/watchlists.js";
import type {
  MembershipRole,
  Watchlist,
} from "../lib/api-modules/types.js";
import { ErrorMessage } from "../components/auth/error-message.js";
import { ConfirmButton } from "../components/confirm-button.js";
import { Toast, useToast } from "../components/toast.js";
import { NotesPanel } from "../components/notes-panel.js";
import { DecisionsPanel } from "../components/decisions-panel.js";

/**
 * /watchlists — household-scoped CRUD for named ticker lists.
 *
 * Read access for every member; write access (create/edit/delete) for
 * owner/admin/member/contributor. Viewer + auditor see the list with
 * no write controls and an explainer banner.
 *
 * Delete is soft (sets is_archived=true server-side) and uses the
 * confirm-then-execute pattern from /settings/household so there's no
 * "are you sure" modal to dismiss.
 */

const READ_ONLY_ROLES: ReadonlySet<MembershipRole> = new Set([
  "viewer",
  "auditor",
]);

export function WatchlistsPage() {
  const { user } = useAuth();
  if (!user) return null;
  const canWrite = !READ_ONLY_ROLES.has(user.role);

  return (
    <div className="mx-auto max-w-4xl p-6">
      <header className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Watchlists</h1>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
            Named lists of tickers your household tracks.
          </p>
        </div>
      </header>

      {!canWrite && (
        <p className="mb-6 rounded-md border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400">
          Read-only access — ask an owner or admin to make changes.
        </p>
      )}

      {canWrite && <CreateWatchlistForm />}
      <WatchlistsList canWrite={canWrite} />
    </div>
  );
}

// === List ==============================================================

function WatchlistsList({ canWrite }: { canWrite: boolean }) {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["watchlists"],
    queryFn: listWatchlists,
  });

  return (
    <section aria-labelledby="watchlists-heading" className="mt-6">
      <h2 id="watchlists-heading" className="mb-3 text-lg font-medium">
        Your watchlists
      </h2>
      {isPending && <p className="text-sm text-zinc-500">Loading…</p>}
      {isError && (
        <ErrorMessage>
          Couldn't load watchlists:{" "}
          {error instanceof Error ? error.message : "unknown"}
        </ErrorMessage>
      )}
      {data && data.watchlists.length === 0 && (
        <p className="text-sm text-zinc-500">No watchlists yet.</p>
      )}
      {data && data.watchlists.length > 0 && (
        <ul className="space-y-3">
          {data.watchlists.map((w) => (
            <WatchlistCard key={w.watchlist_id} w={w} canWrite={canWrite} />
          ))}
        </ul>
      )}
    </section>
  );
}

function WatchlistCard({
  w,
  canWrite,
}: {
  w: Watchlist;
  canWrite: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  if (editing) {
    return (
      <li className="rounded-md border border-zinc-200 p-4 dark:border-zinc-800">
        <EditWatchlistForm w={w} onDone={() => setEditing(false)} />
      </li>
    );
  }
  return (
    <li className="rounded-md border border-zinc-200 p-4 dark:border-zinc-800">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <h3 className="font-medium">{w.name}</h3>
          {w.description && (
            <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
              {w.description}
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {w.tickers.length === 0 && (
              <span className="text-xs text-zinc-500">(no tickers)</span>
            )}
            {w.tickers.map((t) => (
              <Link
                key={t}
                to={`/chart/${encodeURIComponent(t)}`}
                className="rounded-md bg-zinc-100 px-2 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700"
              >
                {t}
              </Link>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setShowNotes((v) => !v)}
            className="mt-3 text-xs text-brand-600 hover:underline dark:text-brand-500"
            data-testid={`watchlist-notes-toggle-${w.watchlist_id}`}
          >
            {showNotes ? "Hide notes" : "Show notes"}
          </button>
          {showNotes && (
            <>
              <NotesPanel
                entityType="watchlist"
                entityId={w.watchlist_id}
                canWriteOverride={canWrite}
              />
              <DecisionsPanel
                entityType="watchlist"
                entityId={w.watchlist_id}
                canWriteOverride={canWrite}
              />
            </>
          )}
        </div>
        {canWrite && (
          <div className="flex shrink-0 flex-col items-end gap-2">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="rounded-md border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
            >
              Edit
            </button>
            <DeleteButton watchlist={w} />
          </div>
        )}
      </div>
    </li>
  );
}

function DeleteButton({ watchlist }: { watchlist: Watchlist }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => deleteWatchlist(watchlist.watchlist_id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["watchlists"] });
    },
  });
  return (
    <ConfirmButton
      label="Delete"
      pendingLabel="Deleting…"
      onConfirm={() => mutation.mutate()}
      isPending={mutation.isPending}
    />
  );
}

// === Create form =======================================================

function CreateWatchlistForm() {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tickersRaw, setTickersRaw] = useState("");
  const [inlineError, setInlineError] = useState<string | null>(null);
  const toast = useToast();

  const mutation = useMutation({
    mutationFn: () =>
      createWatchlist({
        name,
        ...(description ? { description } : {}),
        tickers: normalizeTickerInput(tickersRaw),
      }),
    onSuccess: async (created) => {
      setName("");
      setDescription("");
      setTickersRaw("");
      toast.show(`Created "${created.name}"`);
      await queryClient.invalidateQueries({ queryKey: ["watchlists"] });
    },
    onError: (err) => {
      setInlineError(
        err instanceof ApiError
          ? `${err.message} (code: ${err.code})`
          : "Couldn't create. Try again.",
      );
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInlineError(null);
    mutation.mutate();
  }

  return (
    <section aria-labelledby="create-heading" className="mt-2">
      <h2 id="create-heading" className="mb-3 text-lg font-medium">
        New watchlist
      </h2>
      <form
        onSubmit={onSubmit}
        className="flex flex-col gap-3 rounded-lg border border-zinc-200 p-4 dark:border-zinc-800"
      >
        <div>
          <label htmlFor="wl-name" className="mb-1 block text-sm font-medium">
            Name
          </label>
          <input
            id="wl-name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
            disabled={mutation.isPending}
          />
        </div>
        <div>
          <label
            htmlFor="wl-description"
            className="mb-1 block text-sm font-medium"
          >
            Description <span className="text-zinc-500">(optional)</span>
          </label>
          <input
            id="wl-description"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
            disabled={mutation.isPending}
          />
        </div>
        <div>
          <label
            htmlFor="wl-tickers"
            className="mb-1 block text-sm font-medium"
          >
            Tickers <span className="text-zinc-500">(comma-separated)</span>
          </label>
          <input
            id="wl-tickers"
            type="text"
            value={tickersRaw}
            placeholder="AAPL, NVDA, TSLA"
            onChange={(e) => setTickersRaw(e.target.value)}
            className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
            disabled={mutation.isPending}
          />
        </div>
        <div>
          <button
            type="submit"
            disabled={mutation.isPending || name.length === 0}
            className="rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {mutation.isPending ? "Creating…" : "Create watchlist"}
          </button>
        </div>
      </form>
      {inlineError && (
        <div className="mt-3">
          <ErrorMessage>{inlineError}</ErrorMessage>
        </div>
      )}
      <Toast message={toast.message} />
    </section>
  );
}

// === Edit form (inline) ================================================

function EditWatchlistForm({
  w,
  onDone,
}: {
  w: Watchlist;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(w.name);
  const [description, setDescription] = useState(w.description ?? "");
  const [tickersRaw, setTickersRaw] = useState(w.tickers.join(", "));
  const [inlineError, setInlineError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      updateWatchlist(w.watchlist_id, {
        name,
        description,
        tickers: normalizeTickerInput(tickersRaw),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["watchlists"] });
      onDone();
    },
    onError: (err) => {
      setInlineError(
        err instanceof ApiError
          ? `${err.message} (code: ${err.code})`
          : "Couldn't update. Try again.",
      );
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInlineError(null);
    mutation.mutate();
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-3">
      <div>
        <label
          htmlFor={`wl-edit-name-${w.watchlist_id}`}
          className="mb-1 block text-sm font-medium"
        >
          Name
        </label>
        <input
          id={`wl-edit-name-${w.watchlist_id}`}
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
          disabled={mutation.isPending}
        />
      </div>
      <div>
        <label
          htmlFor={`wl-edit-desc-${w.watchlist_id}`}
          className="mb-1 block text-sm font-medium"
        >
          Description
        </label>
        <input
          id={`wl-edit-desc-${w.watchlist_id}`}
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
          disabled={mutation.isPending}
        />
      </div>
      <div>
        <label
          htmlFor={`wl-edit-tickers-${w.watchlist_id}`}
          className="mb-1 block text-sm font-medium"
        >
          Tickers <span className="text-zinc-500">(comma-separated)</span>
        </label>
        <input
          id={`wl-edit-tickers-${w.watchlist_id}`}
          type="text"
          value={tickersRaw}
          onChange={(e) => setTickersRaw(e.target.value)}
          className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
          disabled={mutation.isPending}
        />
      </div>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={mutation.isPending || name.length === 0}
          className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          onClick={onDone}
          disabled={mutation.isPending}
          className="rounded-md border border-zinc-300 px-3 py-1.5 text-sm hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
        >
          Cancel
        </button>
      </div>
      {inlineError && <ErrorMessage>{inlineError}</ErrorMessage>}
    </form>
  );
}
